/**
 * Processing timeline derived from the source's current `status` + backend `stage`
 * (`app/rag/ingest/progress.py`). The API exposes no per-stage timestamps or durations, so each
 * step reports a state only; the first/last steps carry the source's created/updated times.
 */
export const TIMELINE_STEPS = ["uploaded", "parsed", "chunked", "contextualized", "embedded", "indexed"] as const;
export type TimelineStep = (typeof TIMELINE_STEPS)[number];
export type StepState = "done" | "running" | "pending" | "failed";

const STAGE_STEP: Readonly<Record<string, TimelineStep>> = {
  upload: "uploaded",
  detect: "parsed",
  parse: "parsed",
  images: "parsed",
  normalize_chunk: "chunked",
  enrich: "contextualized",
  embed: "embedded",
  index: "indexed",
  finalize: "indexed",
};

export interface TimelineEntry {
  step: TimelineStep;
  state: StepState;
}

export function timelineFor(status: string, stage: string | null | undefined): TimelineEntry[] {
  if (status === "ready") return TIMELINE_STEPS.map((step) => ({ step, state: "done" }));
  const current = status === "queued" ? "uploaded" : ((stage ? STAGE_STEP[stage] : undefined) ?? "parsed");
  const index = TIMELINE_STEPS.indexOf(current);
  const active: StepState = status === "failed" ? "failed" : "running";
  return TIMELINE_STEPS.map((step, i) => ({ step, state: i < index ? "done" : i === index ? active : "pending" }));
}
